
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import { AgentService } from "@/services/agent-service";
import { WalletService } from "@/services/wallet-service";
import { PurchaseService } from "@/services/purchase-service";
import { Agent } from "@/models/agent";
import { AgentSkeleton } from "@/components/agent-profile/AgentSkeleton";
import { AgentNotFound } from "@/components/agent-profile/AgentNotFound";
import { AgentHeader } from "@/components/agent-profile/AgentHeader";
import { AgentPurchaseCard } from "@/components/agent-profile/AgentPurchaseCard";
import { AgentTags } from "@/components/agent-profile/AgentTags";
import { AgentDetails } from "@/components/agent-profile/AgentDetails";
import { SimilarAgents } from "@/components/agent-profile/SimilarAgents";

const AgentProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  
  const [agent, setAgent] = useState<Agent | null>(null);
  const [loading, setLoading] = useState(true);
  const [similarAgents, setSimilarAgents] = useState<Agent[]>([]);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);

  useEffect(() => {
    const loadAgent = async () => {
      setLoading(true);
      try {
        if (id) {
          const data = await AgentService.getAgentById(id);
          setAgent(data);
          
          if (data) {
            // Load similar agents by category
            const allAgents = await AgentService.getAgents();
            const filtered = allAgents
              .filter(a => a.id !== data.id && a.category === data.category)
              .slice(0, 3);
            setSimilarAgents(filtered);
          }
        }
      } catch (error) {
        console.error("Failed to load agent:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, [id]);

  useEffect(() => {
    const loadUserData = async () => {
      if (user && agent) {
        try {
          // Load wallet data
          const wallet = await WalletService.getWallet(user.id);
          if (wallet) {
            setWalletBalance(wallet.balance);
          }
          
          // Check if user has purchased this agent
          const hasPurchased = await PurchaseService.hasPurchasedAgent(user.id, agent.id);
          setPurchaseSuccess(hasPurchased);
        } catch (error) {
          console.error("Failed to load user data:", error);
        }
      }
    };
    
    loadUserData();
  }, [user, agent]);

  if (loading) {
    return <AgentSkeleton />;
  }

  if (!agent) {
    return <AgentNotFound />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>{agent.name} - OGMAI</title>
      </Helmet>

      <div className="max-w-5xl mx-auto">
        <Button variant="ghost" size="sm" asChild className="mb-6">
          <Link to="/browse" className="flex items-center">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back to Browse
          </Link>
        </Button>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden mb-8">
          <div className="p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex-1">
                <AgentHeader agent={agent} />
                <AgentTags agent={agent} />
              </div>
              
              <AgentPurchaseCard 
                agent={agent}
                user={user}
                walletBalance={walletBalance}
                setWalletBalance={setWalletBalance}
                purchaseSuccess={purchaseSuccess}
                setPurchaseSuccess={setPurchaseSuccess}
              />
            </div>
          </div>
        </div>

        <AgentDetails agent={agent} />
        <SimilarAgents agents={similarAgents} />
      </div>
    </div>
  );
};

export default AgentProfile;
